import { useEffect, useState } from 'react';
import { customerPortalAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  TicketIcon, Plus, Clock, CheckCircle, AlertCircle, MessageCircle,
  Send, Loader2, HelpCircle, Wrench, CreditCard, Package, Lightbulb, ChevronRight, Phone
} from 'lucide-react';

const statusColors = {
  open: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  waiting_customer: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-700'
};

const statusLabels = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_customer: 'Waiting on You',
  resolved: 'Resolved',
  closed: 'Closed'
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600'
};

const categoryIcons = {
  general: HelpCircle,
  technical: Wrench,
  billing: CreditCard,
  product: Package,
  feedback: Lightbulb
};

const categoryDescriptions = {
  general: 'General questions or inquiries',
  technical: 'Technical issues or errors',
  billing: 'Billing and payment questions',
  product: 'Product-related questions',
  feedback: 'Suggestions and feedback'
};

/**
 * CustomerPortalTicketsPage - Support ticket submission and tracking
 */
const CustomerPortalTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [officeStatus, setOfficeStatus] = useState({ isOpen: false, message: '' });
  
  // Submit form state
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  useEffect(() => {
    fetchTickets();
    checkOfficeHours();
    const interval = setInterval(checkOfficeHours, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkOfficeHours = () => {
    const now = new Date();
    const options = { timeZone: 'America/Denver', hour: 'numeric', hour12: false, weekday: 'long' };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);
    let hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    if (hour === 24) hour = 0;
    const dayOfWeek = parts.find(p => p.type === 'weekday')?.value;
    
    const isWeekday = !['Saturday', 'Sunday'].includes(dayOfWeek);
    const isBusinessHours = hour >= 9 && hour < 16;
    const isOpen = isWeekday && isBusinessHours;
    
    let message = isOpen 
      ? 'Our support team is currently available and will respond shortly.'
      : 'Our office is currently closed. Submitted tickets will be addressed during business hours (Mon-Fri, 9 AM - 4 PM MST).';
    
    setOfficeStatus({ isOpen, message });
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await customerPortalAPI.getMyTickets();
      setTickets(res.data.tickets || []);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.subject || !formData.description) {
      toast.error('Please provide a subject and description');
      return;
    }

    try {
      setSubmitting(true);
      const formDataObj = new FormData();
      formDataObj.append('subject', formData.subject);
      formDataObj.append('description', formData.description);
      formDataObj.append('priority', formData.priority);
      formDataObj.append('category', formData.category);
      
      const res = await customerPortalAPI.submitTicket(formDataObj);
      
      if (res.data.success) {
        toast.success('Your support ticket has been submitted!');
        setShowSubmitDialog(false);
        setFormData({
          subject: '',
          description: '',
          priority: 'medium',
          category: 'general'
        });
        fetchTickets();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    
    try {
      setSendingComment(true);
      const formData = new FormData();
      formData.append('message', newComment);
      
      await customerPortalAPI.addTicketComment(selectedTicket.id, formData);
      toast.success('Comment added');
      setNewComment('');
      
      // Refresh ticket
      const res = await customerPortalAPI.getTicket(selectedTicket.id);
      setSelectedTicket(res.data);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSendingComment(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <Loader2 className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  const CategoryIcon = ({ category }) => {
    const Icon = categoryIcons[category] || HelpCircle;
    return <Icon className="h-5 w-5" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-portal-tickets">
      {/* Office Hours Alert */}
      <Alert className={officeStatus.isOpen ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}>
        {officeStatus.isOpen ? (
          <CheckCircle className="h-5 w-5 text-emerald-600" />
        ) : (
          <AlertCircle className="h-5 w-5 text-amber-600" />
        )}
        <AlertTitle className={officeStatus.isOpen ? "text-emerald-800 font-semibold flex items-center gap-2" : "text-amber-800 font-semibold flex items-center gap-2"}>
          <Phone className="h-4 w-4" />
          {officeStatus.isOpen ? "Support Team Available" : "Office Currently Closed"}
        </AlertTitle>
        <AlertDescription className={officeStatus.isOpen ? "text-emerald-700" : "text-amber-700"}>
          {officeStatus.message}
        </AlertDescription>
      </Alert>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Support Tickets</h1>
          <p className="text-muted-foreground">Submit and track your support requests</p>
        </div>
        <Button onClick={() => setShowSubmitDialog(true)} data-testid="new-ticket-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Support Ticket
        </Button>
      </div>

      {/* Quick Help Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                setFormData(prev => ({ ...prev, category: 'technical' }));
                setShowSubmitDialog(true);
              }}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Wrench className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Technical Issue?</h3>
                <p className="text-sm text-blue-700">Report bugs or errors</p>
              </div>
              <ChevronRight className="h-5 w-5 text-blue-400 ml-auto" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                setFormData(prev => ({ ...prev, category: 'billing' }));
                setShowSubmitDialog(true);
              }}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900">Billing Question?</h3>
                <p className="text-sm text-emerald-700">Invoices & payments</p>
              </div>
              <ChevronRight className="h-5 w-5 text-emerald-400 ml-auto" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                setFormData(prev => ({ ...prev, category: 'general' }));
                setShowSubmitDialog(true);
              }}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <HelpCircle className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">General Help</h3>
                <p className="text-sm text-purple-700">Questions & inquiries</p>
              </div>
              <ChevronRight className="h-5 w-5 text-purple-400 ml-auto" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Tickets</CardTitle>
          <CardDescription>Track the status of your support requests</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : tickets.length > 0 ? (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div 
                  key={ticket.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                  data-testid={`ticket-item-${ticket.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      ticket.status === 'open' ? 'bg-amber-100' :
                      ticket.status === 'in_progress' ? 'bg-blue-100' :
                      ticket.status === 'resolved' ? 'bg-emerald-100' : 'bg-slate-100'
                    }`}>
                      <CategoryIcon category={ticket.category} />
                    </div>
                    <div>
                      <p className="font-medium">{ticket.subject}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="capitalize">{ticket.category}</span>
                        <span>•</span>
                        <span>{format(new Date(ticket.created_at), 'MMM d, yyyy')}</span>
                        {ticket.comments?.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" />
                              {ticket.comments.length}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={priorityColors[ticket.priority]}>
                      {ticket.priority}
                    </Badge>
                    <Badge className={`${statusColors[ticket.status]} flex items-center gap-1`}>
                      {getStatusIcon(ticket.status)}
                      {statusLabels[ticket.status]}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TicketIcon className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No tickets yet</h3>
              <p className="text-muted-foreground mb-4">
                Need help? Create a support ticket and our team will assist you
              </p>
              <Button onClick={() => setShowSubmitDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Ticket
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit Ticket Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and our team will get back to you as soon as possible
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                placeholder="Brief summary of your issue"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                data-testid="ticket-subject-input"
              />
            </div>

            {/* Category & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="ticket-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryDescriptions).map(([key, desc]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <CategoryIcon category={key} />
                          <span className="capitalize">{key}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="ticket-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Not urgent</SelectItem>
                    <SelectItem value="medium">Medium - Normal</SelectItem>
                    <SelectItem value="high">High - Important</SelectItem>
                    <SelectItem value="urgent">Urgent - Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, or relevant information..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={5}
                data-testid="ticket-description-input"
              />
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
              <p className="font-medium mb-1">Tips for faster resolution:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-600">
                <li>Include specific error messages or screenshots</li>
                <li>Describe when the issue started</li>
                <li>List any steps you've already tried</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !formData.subject || !formData.description}
              data-testid="submit-ticket-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Ticket
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <Badge className={statusColors[selectedTicket?.status]}>
                {statusLabels[selectedTicket?.status]}
              </Badge>
              <Badge className={priorityColors[selectedTicket?.priority]}>
                {selectedTicket?.priority}
              </Badge>
            </div>
            <DialogTitle>{selectedTicket?.subject}</DialogTitle>
            <DialogDescription>
              Submitted on {selectedTicket && format(new Date(selectedTicket.created_at), 'MMMM d, yyyy h:mm a')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Original Description */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-500 mb-2">Original Request</p>
              <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket?.description}</p>
            </div>

            {/* Comments Thread */}
            {selectedTicket?.comments?.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-500">Conversation</p>
                {selectedTicket.comments.map((comment) => (
                  <div 
                    key={comment.id}
                    className={`p-4 rounded-lg ${
                      comment.is_customer 
                        ? 'bg-blue-50 border border-blue-100 ml-8' 
                        : 'bg-emerald-50 border border-emerald-100 mr-8'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-sm font-medium ${
                        comment.is_customer ? 'text-blue-700' : 'text-emerald-700'
                      }`}>
                        {comment.is_customer ? 'You' : comment.author_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{comment.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Comment */}
          {selectedTicket?.status !== 'closed' && selectedTicket?.status !== 'resolved' && (
            <div className="border-t pt-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Type your reply..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  className="flex-1"
                />
                <Button 
                  onClick={handleAddComment}
                  disabled={sendingComment || !newComment.trim()}
                >
                  {sendingComment ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {(selectedTicket?.status === 'closed' || selectedTicket?.status === 'resolved') && (
            <div className="border-t pt-4 text-center text-sm text-muted-foreground">
              This ticket has been {selectedTicket.status}. Create a new ticket if you need further assistance.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalTicketsPage;
